
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export interface RentalDeletionStats {
  trackingPoints: number;
  payments: number;
  subscriptions: number;
  rentalId: string;
  humanNumber: string;
}

/**
 * Получает статистику связанных данных для аренды
 */
export const getRentalDeletionStats = async (rentalId: string): Promise<RentalDeletionStats | null> => {
  try {
    // Получаем основную информацию об аренде
    const { data: rental, error: rentalError } = await supabase
      .from('rentals')
      .select('human_number')
      .eq('id', rentalId)
      .single();

    if (rentalError) {
      console.error('Ошибка при получении данных аренды:', rentalError);
      return null;
    }

    // Подсчитываем связанные GPS точки
    const { count: trackingCount, error: trackingError } = await supabase
      .from('bike_tracking_history')
      .select('*', { count: 'exact', head: true })
      .eq('rental_id', rentalId);

    if (trackingError) {
      console.error('Ошибка при подсчете GPS точек:', trackingError);
    }

    // Подсчитываем связанные платежи
    const { count: paymentsCount, error: paymentsError } = await supabase
      .from('payments')
      .select('*', { count: 'exact', head: true })
      .eq('rental_id', rentalId);

    if (paymentsError) {
      console.error('Ошибка при подсчете платежей:', paymentsError);
    }

    // Подсчитываем связанные подписки
    const { count: subscriptionsCount, error: subscriptionsError } = await supabase
      .from('subscriptions')
      .select('*', { count: 'exact', head: true })
      .eq('rental_id', rentalId);

    if (subscriptionsError) {
      console.error('Ошибка при подсчете подписок:', subscriptionsError);
    }

    return {
      trackingPoints: trackingCount || 0,
      payments: paymentsCount || 0,
      subscriptions: subscriptionsCount || 0,
      rentalId,
      humanNumber: rental.human_number || rentalId.substring(0, 8)
    };
  } catch (error) {
    console.error('Ошибка при получении статистики удаления:', error);
    return null;
  }
};

/**
 * Каскадное удаление аренды со всеми связанными данными
 */
export const deleteRentalWithHistory = async (rentalId: string): Promise<{ success: boolean; error: any }> => {
  try {
    console.log(`Начинаем каскадное удаление аренды ${rentalId}`);

    // 1. Удаляем GPS историю и данные пробега
    console.log('Удаление GPS истории...');
    const { data: trackingData, error: trackingError } = await supabase
      .from('bike_tracking_history')
      .delete()
      .eq('rental_id', rentalId)
      .select();

    if (trackingError) {
      console.error('Ошибка при удалении GPS истории:', trackingError);
      return { success: false, error: trackingError };
    }
    console.log(`Удалено ${trackingData?.length || 0} GPS записей`);

    // 2. Удаляем связанные платежи
    console.log('Удаление платежей...');
    const { data: paymentsData, error: paymentsError } = await supabase
      .from('payments')
      .delete()
      .eq('rental_id', rentalId)
      .select();

    if (paymentsError) {
      console.error('Ошибка при удалении платежей:', paymentsError);
      return { success: false, error: paymentsError };
    }
    console.log(`Удалено ${paymentsData?.length || 0} платежей`);

    // 3. Удаляем связанные подписки
    console.log('Удаление подписок...');
    const { data: subscriptionsData, error: subscriptionsError } = await supabase
      .from('subscriptions')
      .delete()
      .eq('rental_id', rentalId)
      .select();

    if (subscriptionsError) {
      console.error('Ошибка при удалении подписок:', subscriptionsError);
      return { success: false, error: subscriptionsError };
    }
    console.log(`Удалено ${subscriptionsData?.length || 0} подписок`);

    // 4. Удаляем саму аренду
    console.log('Удаление аренды...');
    const { data: rentalData, error: rentalError } = await supabase
      .from('rentals')
      .delete()
      .eq('id', rentalId)
      .select();

    if (rentalError) {
      console.error('Ошибка при удалении аренды:', rentalError);
      return { success: false, error: rentalError };
    }
    console.log(`Аренда удалена: ${JSON.stringify(rentalData)}`);

    // Логируем успешное завершение
    console.log(`Каскадное удаление аренды ${rentalId} завершено успешно`);
    
    return { success: true, error: null };
  } catch (error: any) {
    console.error('Непредвиденная ошибка при каскадном удалении аренды:', error);
    return { success: false, error };
  }
};
